import { useQueryClient } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";
import { useCallback } from "react";

import { useBankStatementSubmitByUuidMutation } from "../queries/bank-connect/bank-statement.mutations";
import {
  getUnsubmittedUploadGroups,
  groupedBankingSourcesQueryOptions,
  invalidateBankingSourceQueries,
} from "../queries/bank-connect/queries";

// A page that already holds a fresh useSuspenseQuery for this same key (e.g. the accounts page,
// moments before calling this) can skip the network round-trip this would otherwise repeat.
const RECENTLY_FETCHED_STALE_TIME_MS = 10_000;

export const useSubmitUnsubmittedBankStatements = (customerDomain: string) => {
  const queryClient = useQueryClient();
  const submitByUuid = useBankStatementSubmitByUuidMutation(customerDomain);
  const posthog = usePostHog();

  return useCallback(async () => {
    const { data: groupedBankingSources } = await queryClient.fetchQuery({
      ...groupedBankingSourcesQueryOptions(customerDomain),
      staleTime: RECENTLY_FETCHED_STALE_TIME_MS,
    });

    const unsubmittedGroups = getUnsubmittedUploadGroups(groupedBankingSources);
    if (unsubmittedGroups.length === 0) {
      return;
    }

    await Promise.all(unsubmittedGroups.map(({ group, uuids }) => submitByUuid.mutateAsync({ uuids, group })));
    posthog?.capture("onboarding_bank_statement_submitted", {
      file_count: unsubmittedGroups.reduce((count, { uuids }) => count + uuids.length, 0),
      trigger: "auto_submit_on_next",
    });

    // Awaited so the workflow tracker's completion status (which drives its step icon) is
    // up to date before the caller navigates away - otherwise the next page briefly shows
    // the banking step as incomplete until this refetch lands.
    await invalidateBankingSourceQueries(queryClient, customerDomain);
  }, [queryClient, submitByUuid, customerDomain, posthog]);
};
